import { AfriexAdminPermission, AfriexRoles } from '../domain/enums/index.js';
import { isEnumValue } from './isEnumValue.js';
/**
 * The permissions each staff role holds. Most grants are the server's: they
 * were built from the roles each admin route allowed on 2026-10-29. The
 * exceptions are the routes that were open to every staff role while the admin
 * dashboard hid their controls (business wallet, refunds, flags, fees, limits,
 * user block and the like): those permissions took the dashboard's roles for
 * the controls that call them. It exists so the server's route guards and the
 * admin dashboard's gating read one table, and a screen never offers an action
 * its endpoint would refuse.
 *
 * Engineering Manager is the standing break-glass role, so it holds every
 * permission, including any added later. Keep each other list in the enum's
 * order so a diff shows a grant being added or removed rather than reshuffled.
 */
export const adminRolePermissions = {
    [AfriexRoles.USER]: [],
    [AfriexRoles.ENGINEERING_MANAGER]: Object.values(AfriexAdminPermission),
    [AfriexRoles.ENGINEERING_MEMBER]: [
        AfriexAdminPermission.UserTierInfoRead,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserBlock,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.TransactionRefund,
        AfriexAdminPermission.TransactionReview,
        AfriexAdminPermission.TransactionLedgerCreate,
        AfriexAdminPermission.TransactionRewardedRead,
        AfriexAdminPermission.TransactionPlaidRead,
        AfriexAdminPermission.TransactionFeeRead,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.RatesUpload,
        AfriexAdminPermission.RatesHistoryRead,
        AfriexAdminPermission.RatePromoManage,
        AfriexAdminPermission.ReferralPayoutManage,
        AfriexAdminPermission.ReferralManualPayoutCreate,
        AfriexAdminPermission.ReferralTierUpdate,
        AfriexAdminPermission.ProcessorTest,
        AfriexAdminPermission.ProcessorDowntimeNotify,
        AfriexAdminPermission.CountryUpdate,
        AfriexAdminPermission.CurrencyUpdate,
        AfriexAdminPermission.BusinessCountryUpdate,
        AfriexAdminPermission.BusinessDangerZoneManage,
        AfriexAdminPermission.QueueDashboardRead,
    ],
    [AfriexRoles.CUSTOMER_SUPPORT_MANAGER]: [
        AfriexAdminPermission.UserUpdate,
        AfriexAdminPermission.UserRoleUpdate,
        AfriexAdminPermission.UserTierInfoRead,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserBlock,
        AfriexAdminPermission.TransactionSwapCreate,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.BlockedPaymentMethodRead,
        AfriexAdminPermission.BlockedPaymentMethodManage,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.TransactionRefund,
        AfriexAdminPermission.TransactionReview,
        AfriexAdminPermission.TransactionLedgerCreate,
        AfriexAdminPermission.TransactionRewardedRead,
        AfriexAdminPermission.TransactionFeeRead,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.ReferralTierUpdate,
        AfriexAdminPermission.CountryUpdate,
        AfriexAdminPermission.CurrencyUpdate,
        AfriexAdminPermission.BusinessCountryUpdate,
        AfriexAdminPermission.BusinessWalletUpdate,
        AfriexAdminPermission.BusinessOnboardingManage,
    ],
    [AfriexRoles.CUSTOMER_SUPPORT_MEMBER]: [
        AfriexAdminPermission.UserUpdate,
        AfriexAdminPermission.UserTierInfoRead,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserBlock,
        AfriexAdminPermission.TransactionSwapCreate,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.BlockedPaymentMethodRead,
        AfriexAdminPermission.BlockedPaymentMethodManage,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.TransactionRefund,
        AfriexAdminPermission.TransactionRewardedRead,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.BusinessOnboardingManage,
    ],
    [AfriexRoles.COMPLIANCE_MEMBER]: [
        AfriexAdminPermission.UserUpdate,
        AfriexAdminPermission.UserTransactionLimitUpdate,
        AfriexAdminPermission.UserDeactivate,
        AfriexAdminPermission.UserRoleUpdate,
        AfriexAdminPermission.UserTierInfoRead,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserBlock,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.KYCUpdate,
        AfriexAdminPermission.KYCVerificationCreate,
        AfriexAdminPermission.BusinessDocumentRequestCreate,
        AfriexAdminPermission.RFIRead,
        AfriexAdminPermission.RFIManage,
        AfriexAdminPermission.BusinessVirtualAccountApplicationRead,
        AfriexAdminPermission.BusinessVirtualAccountApplicationReview,
        AfriexAdminPermission.BlockedPaymentMethodRead,
        AfriexAdminPermission.BlockedPaymentMethodManage,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.TransactionRefund,
        AfriexAdminPermission.TransactionReview,
        AfriexAdminPermission.TransactionLedgerCreate,
        AfriexAdminPermission.TransactionFeeRead,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.BusinessKYCUpdate,
        AfriexAdminPermission.BusinessFlagUpdate,
        AfriexAdminPermission.BusinessUserFlagUpdate,
        AfriexAdminPermission.BusinessUserStatementRead,
    ],
    [AfriexRoles.COMPLIANCE_MANAGER]: [
        AfriexAdminPermission.UserUpdate,
        AfriexAdminPermission.UserTransactionLimitUpdate,
        AfriexAdminPermission.UserDeactivate,
        AfriexAdminPermission.UserRoleUpdate,
        AfriexAdminPermission.UserTierInfoRead,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserBlock,
        AfriexAdminPermission.TransactionSwapCreate,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.KYCUpdate,
        AfriexAdminPermission.KYCVerificationCreate,
        AfriexAdminPermission.BusinessDocumentRequestCreate,
        AfriexAdminPermission.RFIRead,
        AfriexAdminPermission.RFIManage,
        AfriexAdminPermission.BusinessVirtualAccountApplicationRead,
        AfriexAdminPermission.BusinessVirtualAccountApplicationReview,
        AfriexAdminPermission.BlockedPaymentMethodRead,
        AfriexAdminPermission.BlockedPaymentMethodManage,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.TransactionRefund,
        AfriexAdminPermission.TransactionReview,
        AfriexAdminPermission.TransactionLedgerCreate,
        AfriexAdminPermission.TransactionFeeRead,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.CountryUpdate,
        AfriexAdminPermission.BusinessCountryUpdate,
        AfriexAdminPermission.BusinessKYCUpdate,
        AfriexAdminPermission.BusinessFlagUpdate,
        AfriexAdminPermission.BusinessUserFlagUpdate,
        AfriexAdminPermission.BusinessUserStatementRead,
    ],
    [AfriexRoles.FINANCE_MANAGER]: [
        AfriexAdminPermission.UserTierInfoRead,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserTierManage,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.RFIRead,
        AfriexAdminPermission.RFIManage,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.TransactionRefund,
        AfriexAdminPermission.TransactionReview,
        AfriexAdminPermission.TransactionLedgerCreate,
        AfriexAdminPermission.TransactionFeeRead,
        AfriexAdminPermission.TransactionFeeCreate,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.RatesUpload,
        AfriexAdminPermission.RatesHistoryRead,
        AfriexAdminPermission.RatePromoManage,
        AfriexAdminPermission.OTCVolumeRead,
        AfriexAdminPermission.OTCVolumeManage,
        AfriexAdminPermission.OTCDeskRead,
        AfriexAdminPermission.OTCDeskManage,
        AfriexAdminPermission.MarketPlaceRead,
        AfriexAdminPermission.MarketPlaceManage,
        AfriexAdminPermission.ReferralPayoutManage,
        AfriexAdminPermission.ReferralManualPayoutCreate,
        AfriexAdminPermission.ReferralTierUpdate,
        AfriexAdminPermission.PaymentMethodCardIssuerUpdate,
        AfriexAdminPermission.ProcessorNameResolutionInvalidate,
        AfriexAdminPermission.CountryUpdate,
        AfriexAdminPermission.CurrencyUpdate,
        AfriexAdminPermission.BusinessCountryUpdate,
        AfriexAdminPermission.BusinessWalletUpdate,
        AfriexAdminPermission.BusinessTransactionUpdate,
        AfriexAdminPermission.BusinessFeeManage,
        AfriexAdminPermission.BusinessPoolAccountManage,
        AfriexAdminPermission.BusinessPostFundingUpdate,
        AfriexAdminPermission.BusinessLiveAccessRead,
        AfriexAdminPermission.BusinessLiveAccessUpdate,
        AfriexAdminPermission.BusinessAgreementRead,
        AfriexAdminPermission.BusinessAgreementUpdate,
        AfriexAdminPermission.BusinessFeeNegotiationRead,
        AfriexAdminPermission.BusinessFeeNegotiationUpdate,
        AfriexAdminPermission.BusinessPoolDepositReview,
        AfriexAdminPermission.BusinessInvoiceManage,
        AfriexAdminPermission.SettlementPaymentMethodManage,
        AfriexAdminPermission.BusinessPinResetRequestReview,
        AfriexAdminPermission.BusinessAffiliateRead,
        AfriexAdminPermission.BusinessAffiliateManage,
        AfriexAdminPermission.SubscriptionPlanRead,
        AfriexAdminPermission.SubscriptionPlanManage,
    ],
    [AfriexRoles.FINANCE_MEMBER]: [
        AfriexAdminPermission.UserTierInfoRead,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserTierManage,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.RFIRead,
        AfriexAdminPermission.RFIManage,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.TransactionRefund,
        AfriexAdminPermission.TransactionReview,
        AfriexAdminPermission.TransactionLedgerCreate,
        AfriexAdminPermission.TransactionFeeRead,
        AfriexAdminPermission.TransactionFeeCreate,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.RatesUpload,
        AfriexAdminPermission.RatesHistoryRead,
        AfriexAdminPermission.RatePromoManage,
        AfriexAdminPermission.OTCVolumeRead,
        AfriexAdminPermission.OTCVolumeManage,
        AfriexAdminPermission.OTCDeskRead,
        AfriexAdminPermission.OTCDeskManage,
        AfriexAdminPermission.MarketPlaceRead,
        AfriexAdminPermission.MarketPlaceManage,
        AfriexAdminPermission.ReferralPayoutManage,
        AfriexAdminPermission.ReferralTierUpdate,
        AfriexAdminPermission.RewardRead,
        AfriexAdminPermission.RewardManage,
        AfriexAdminPermission.PaymentMethodCardIssuerUpdate,
        AfriexAdminPermission.CountryUpdate,
        AfriexAdminPermission.CurrencyUpdate,
        AfriexAdminPermission.BusinessCountryUpdate,
        AfriexAdminPermission.BusinessWalletUpdate,
        AfriexAdminPermission.BusinessTransactionUpdate,
        AfriexAdminPermission.BusinessFeeManage,
        AfriexAdminPermission.BusinessPoolAccountManage,
        AfriexAdminPermission.BusinessPostFundingUpdate,
        AfriexAdminPermission.BusinessLiveAccessRead,
        AfriexAdminPermission.BusinessLiveAccessUpdate,
        AfriexAdminPermission.BusinessAgreementRead,
        AfriexAdminPermission.BusinessAgreementUpdate,
        AfriexAdminPermission.BusinessFeeNegotiationRead,
        AfriexAdminPermission.BusinessFeeNegotiationUpdate,
        AfriexAdminPermission.BusinessPoolDepositReview,
        AfriexAdminPermission.BusinessInvoiceManage,
        AfriexAdminPermission.SettlementPaymentMethodManage,
        AfriexAdminPermission.BusinessPinResetRequestReview,
        AfriexAdminPermission.BusinessAffiliateRead,
        AfriexAdminPermission.BusinessAffiliateManage,
    ],
    [AfriexRoles.GROWTH_MANAGER]: [
        AfriexAdminPermission.UserMarketingContactRead,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.TransactionLedgerCreate,
        AfriexAdminPermission.ReferralTierManage,
        AfriexAdminPermission.ReferralTierUpdate,
        AfriexAdminPermission.CountryUpdate,
        AfriexAdminPermission.CurrencyUpdate,
        AfriexAdminPermission.BusinessCountryUpdate,
        AfriexAdminPermission.BusinessOnboardingManage,
        AfriexAdminPermission.BusinessLiveAccessRead,
        AfriexAdminPermission.BusinessLiveAccessUpdate,
        AfriexAdminPermission.BusinessAgreementRead,
        AfriexAdminPermission.BusinessAgreementUpdate,
        AfriexAdminPermission.BusinessFeeNegotiationRead,
        AfriexAdminPermission.BusinessFeeNegotiationUpdate,
        AfriexAdminPermission.BusinessAffiliateRead,
        AfriexAdminPermission.BusinessAffiliateManage,
        AfriexAdminPermission.BusinessUpdateRead,
        AfriexAdminPermission.BusinessUpdateManage,
    ],
    [AfriexRoles.GROWTH_MEMBER]: [
        AfriexAdminPermission.UserMarketingContactRead,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.ReferralTierManage,
        AfriexAdminPermission.BusinessOnboardingManage,
        AfriexAdminPermission.BusinessLiveAccessRead,
        AfriexAdminPermission.BusinessLiveAccessUpdate,
        AfriexAdminPermission.BusinessAgreementRead,
        AfriexAdminPermission.BusinessAgreementUpdate,
        AfriexAdminPermission.BusinessFeeNegotiationRead,
        AfriexAdminPermission.BusinessFeeNegotiationUpdate,
        AfriexAdminPermission.BusinessAffiliateRead,
        AfriexAdminPermission.BusinessUpdateRead,
        AfriexAdminPermission.BusinessUpdateManage,
    ],
    [AfriexRoles.OPERATIONS_MANAGER]: [
        AfriexAdminPermission.UserTierInfoRead,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserBlock,
        AfriexAdminPermission.TransactionSwapCreate,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.KYCUpdate,
        AfriexAdminPermission.BusinessDocumentRequestCreate,
        AfriexAdminPermission.RFIRead,
        AfriexAdminPermission.RFIManage,
        AfriexAdminPermission.BusinessVirtualAccountApplicationRead,
        AfriexAdminPermission.BusinessVirtualAccountApplicationReview,
        AfriexAdminPermission.BlockedPaymentMethodRead,
        AfriexAdminPermission.BlockedPaymentMethodManage,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.TransactionRefund,
        AfriexAdminPermission.TransactionReview,
        AfriexAdminPermission.TransactionLedgerCreate,
        AfriexAdminPermission.TransactionRewardedRead,
        AfriexAdminPermission.TransactionFeeRead,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.ProcessorDowntimeNotify,
        AfriexAdminPermission.ProcessorNameResolutionInvalidate,
        AfriexAdminPermission.CountryUpdate,
        AfriexAdminPermission.CurrencyUpdate,
        AfriexAdminPermission.BusinessCountryUpdate,
        AfriexAdminPermission.BusinessWalletUpdate,
        AfriexAdminPermission.BusinessTransactionUpdate,
        AfriexAdminPermission.BusinessFeeManage,
        AfriexAdminPermission.BusinessLimitManage,
        AfriexAdminPermission.FeatureManage,
        AfriexAdminPermission.BusinessFlagUpdate,
        AfriexAdminPermission.BusinessUserFlagUpdate,
        AfriexAdminPermission.BusinessUserStatementRead,
        AfriexAdminPermission.BusinessPoolAccountManage,
        AfriexAdminPermission.BusinessFeaturesUpdate,
        AfriexAdminPermission.BusinessPostFundingUpdate,
        AfriexAdminPermission.BusinessLiveAccessRead,
        AfriexAdminPermission.BusinessLiveAccessUpdate,
        AfriexAdminPermission.BusinessAgreementRead,
        AfriexAdminPermission.BusinessAgreementUpdate,
        AfriexAdminPermission.BusinessPoolDepositReview,
        AfriexAdminPermission.BusinessAffiliateRead,
        AfriexAdminPermission.BusinessAffiliateManage,
        AfriexAdminPermission.BusinessUpdateRead,
        AfriexAdminPermission.BusinessUpdateManage,
        AfriexAdminPermission.SubscriptionPlanRead,
        AfriexAdminPermission.SubscriptionPlanManage,
    ],
    [AfriexRoles.OPERATIONS_MEMBER]: [
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserBlock,
        AfriexAdminPermission.TransactionSwapCreate,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.RFIRead,
        AfriexAdminPermission.RFIManage,
        AfriexAdminPermission.BlockedPaymentMethodRead,
        AfriexAdminPermission.BlockedPaymentMethodManage,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.TransactionRefund,
        AfriexAdminPermission.TransactionReview,
        AfriexAdminPermission.TransactionLedgerCreate,
        AfriexAdminPermission.TransactionRewardedRead,
        AfriexAdminPermission.TransactionFeeRead,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.ProcessorDowntimeNotify,
        AfriexAdminPermission.BusinessWalletUpdate,
        AfriexAdminPermission.BusinessTransactionUpdate,
        AfriexAdminPermission.BusinessFlagUpdate,
        AfriexAdminPermission.BusinessUserStatementRead,
        AfriexAdminPermission.BusinessPoolAccountManage,
        AfriexAdminPermission.BusinessLiveAccessRead,
        AfriexAdminPermission.BusinessLiveAccessUpdate,
        AfriexAdminPermission.BusinessAgreementRead,
        AfriexAdminPermission.BusinessAgreementUpdate,
        AfriexAdminPermission.BusinessPoolDepositReview,
        AfriexAdminPermission.BusinessAffiliateRead,
        AfriexAdminPermission.BusinessUpdateRead,
        AfriexAdminPermission.BusinessUpdateManage,
    ],
    [AfriexRoles.PEOPLES_OPERATIONS_MANAGER]: [
        AfriexAdminPermission.CountryUpdate,
        AfriexAdminPermission.BusinessCountryUpdate,
    ],
    [AfriexRoles.PEOPLES_OPERATIONS_MEMBER]: [],
    [AfriexRoles.PRODUCT_ENG_QA_DESIGN_MEMBER]: [
        AfriexAdminPermission.UserTransactionLimitUpdate,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserBlock,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.ReferralTierManage,
        AfriexAdminPermission.RewardRead,
        AfriexAdminPermission.RewardManage,
        AfriexAdminPermission.BusinessDangerZoneManage,
        AfriexAdminPermission.BusinessLiveAccessRead,
        AfriexAdminPermission.BusinessLiveAccessUpdate,
        AfriexAdminPermission.BusinessAgreementRead,
        AfriexAdminPermission.BusinessAgreementUpdate,
        AfriexAdminPermission.BusinessUpdateRead,
        AfriexAdminPermission.BusinessUpdateManage,
    ],
    [AfriexRoles.PRODUCT_ENG_QA_DESIGN_MANAGER]: [
        AfriexAdminPermission.UserTransactionLimitUpdate,
        AfriexAdminPermission.UserTransactionRead,
        AfriexAdminPermission.UserStatementRead,
        AfriexAdminPermission.UserBlock,
        AfriexAdminPermission.SecurityNoteRead,
        AfriexAdminPermission.SecurityNoteCreate,
        AfriexAdminPermission.PushNotificationSend,
        AfriexAdminPermission.TransactionRead,
        AfriexAdminPermission.DisputeRead,
        AfriexAdminPermission.DisputeUpdate,
        AfriexAdminPermission.RatesUpload,
        AfriexAdminPermission.RatesHistoryRead,
        AfriexAdminPermission.RatePromoManage,
        AfriexAdminPermission.ReferralTierManage,
        AfriexAdminPermission.ReferralTierUpdate,
        AfriexAdminPermission.RewardRead,
        AfriexAdminPermission.RewardManage,
        AfriexAdminPermission.ProcessorNameResolutionInvalidate,
        AfriexAdminPermission.CountryUpdate,
        AfriexAdminPermission.CurrencyUpdate,
        AfriexAdminPermission.BusinessCountryUpdate,
        AfriexAdminPermission.BusinessFeeManage,
        AfriexAdminPermission.BusinessLimitManage,
        AfriexAdminPermission.FeatureManage,
        AfriexAdminPermission.BusinessFlagUpdate,
        AfriexAdminPermission.BusinessUserFlagUpdate,
        AfriexAdminPermission.BusinessOnboardingManage,
        AfriexAdminPermission.BusinessPoolAccountManage,
        AfriexAdminPermission.BusinessFeaturesUpdate,
        AfriexAdminPermission.BusinessDangerZoneManage,
        AfriexAdminPermission.BusinessLiveAccessRead,
        AfriexAdminPermission.BusinessLiveAccessUpdate,
        AfriexAdminPermission.BusinessAgreementRead,
        AfriexAdminPermission.BusinessAgreementUpdate,
        AfriexAdminPermission.BusinessUpdateRead,
        AfriexAdminPermission.BusinessUpdateManage,
        AfriexAdminPermission.SubscriptionPlanRead,
        AfriexAdminPermission.SubscriptionPlanManage,
    ],
};
/**
 * Whether a staff role holds a permission. Takes the role as a string because
 * both callers hold it as one (the token's `role` claim on the server, the
 * stored role in the admin dashboard); anything that is not a staff role holds
 * nothing.
 */
export const hasAdminPermission = (role, permission) => isEnumValue(AfriexRoles, role) &&
    adminRolePermissions[role].includes(permission);
//# sourceMappingURL=adminRolePermissions.js.map